import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown, Search } from 'lucide-react'
import { Input, Select, Button } from '@/components/ui'
import { Tabs } from '@/components/ui/Tabs'
import type { SelectOption } from '@/components/ui/Select'

export interface CustomerFiltersProps {
  status: string
  onStatusChange: (value: string) => void
  search: string
  onSearchChange: (value: string) => void
  sort: string
  order: 'asc' | 'desc'
  onSortChange: (key: string) => void
  onToggleOrder: () => void
  sortOptions: SelectOption[]
  /** Show the lead-gen `external` status tab (dual-gate resolved by the caller). */
  showExternalTab: boolean
}

/**
 * Customer list filter bar (EP-109). Status tabs + a full-width label-less
 * search grouped in one card, then a large "Recent" heading with compact sort
 * controls sitting above the card grid — matching the customers mockup.
 * Extracted as a dedicated component so the list page owns only state, not
 * filter chrome.
 *
 * NOTE (intentional, accepted 2026-07-30): the status filter is deliberately
 * duplicated — the segmented `Tabs` AND the status `Select` in the Recent row
 * (both bound to the same `status` state) — because this page follows the newer
 * EP-109 redesign, which is a transitional/redundant-but-fine state. Do NOT
 * "fix" the redundancy as if it were a bug; the owner has blessed keeping both.
 */
export function CustomerFilters({
  status,
  onStatusChange,
  search,
  onSearchChange,
  sort,
  order,
  onSortChange,
  onToggleOrder,
  sortOptions,
  showExternalTab,
}: CustomerFiltersProps) {
  const { t } = useTranslation('customers')
  const { t: tc } = useTranslation('common')

  const orderLabel = order === 'asc' ? tc('sort.order_asc') : tc('sort.order_desc')

  // Status dropdown options — mirror the tabs (synced to the same `status`
  // state), matching the Figma sort row. The `external` option is dual-gated.
  const statusFilterOptions = [
    { value: '', label: t('status.all') },
    { value: 'active', label: t('status.active') },
    { value: 'prospect', label: t('status.prospect') },
    { value: 'alumni', label: t('status.alumni') },
    ...(showExternalTab ? [{ value: 'external', label: t('status.external') }] : []),
  ]

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        {/* Status tabs — segmented control. The `external` option is gated to
            the lead-gen dual-gate. Tabs' mobileSelect default renders a Select
            fallback below sm. */}
        <Tabs
          variant="bordered"
          value={status}
          onChange={onStatusChange}
          options={[
            { value: '', label: t('tabs.all') },
            { value: 'active', label: t('tabs.active') },
            { value: 'prospect', label: t('tabs.prospects') },
            { value: 'alumni', label: t('tabs.alumni') },
            ...(showExternalTab ? [{ value: 'external', label: t('tabs.external') }] : []),
          ]}
        />

        {/* Full-width, label-less search (mockup): icon inside, no field label. */}
        <Input
          aria-label={tc('actions.search')}
          leftIcon={<Search className="w-4 h-4" />}
          placeholder={t('placeholders.search')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      {/* "Recent" heading + compact sort controls, above the grid (mockup). */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl sm:text-2xl font-bold">{t('sections.recent')}</h2>
        <div className="flex flex-wrap items-center gap-2">
          {/* Status dropdown — synced with the tabs (Figma sort row). */}
          <Select
            data-testid="customer-status-select"
            aria-label={t('labels.status')}
            size="sm"
            value={status}
            onChange={(e) => onStatusChange(e.target.value)}
            options={statusFilterOptions}
            className="min-w-0"
          />
          <Select
            data-testid="customer-sort-select"
            aria-label={tc('sort.label')}
            size="sm"
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            options={sortOptions}
            className="min-w-0"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={orderLabel}
            onClick={onToggleOrder}
            leftIcon={order === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
            className="shrink-0"
          >
            <span className="hidden sm:inline">{orderLabel}</span>
          </Button>
        </div>
      </div>
    </div>
  )
}
